import { matchRoutes, type RouteObject } from 'react-router';

// Authoritative flat list of matchable route patterns, mirrored from src/app/routes.tsx.
// Params are kept symbolic (never resolved) so no ids/PHI leak into analytics.
//
// Excluded intentionally: the "/" and "/:orgId" index routes. Both render a
// redirect-only component (RedirectToSelectedOrg / OrgRedirect) that never
// stays on screen, so there is no distinct content pattern to attribute a
// pageview to. The wildcard fallback ("*") is likewise a redirect, not a page.
const ANALYTICS_ROUTE_PATTERNS: string[] = [
  // public
  '/login',
  '/signup',
  '/complete-signup',
  '/reset-password',
  '/verify-email',
  '/sso/callback/:connectionId',
  '/auth/callback/:connectionId',
  '/logout',
  // org-scoped
  '/:orgId/qc',
  '/:orgId/qc/:evaluationId',
  '/:orgId/analytics',
  '/:orgId/analytics/agents/:agentId',
  '/:orgId/cases-analytics',
  '/:orgId/webhooks',
  '/:orgId/webhooks/endpoints/:endpointId',
  '/:orgId/workforce',
  '/:orgId/my-cases',
  '/:orgId/my-tasks',
  '/:orgId/all-tasks',
  '/:orgId/cases',
  // program-scoped
  '/:orgId/:progId/cases',
  '/:orgId/:progId/cases/:caseId',
  '/:orgId/:progId/cases/:caseId/calls/:callId/console',
  '/:orgId/:progId/create-case',
  '/:orgId/:progId/tasks',
  '/:orgId/:progId/campaigns',
  '/:orgId/:progId/campaigns/:campaignId',
  '/:orgId/:progId/calls',
  '/:orgId/:progId/mailbox',
  '/:orgId/:progId/chat',
  '/:orgId/:progId/settings',
];

const ROUTE_OBJECTS: RouteObject[] = ANALYTICS_ROUTE_PATTERNS.map(path => ({ path }));

/**
 * Resolves a live pathname to its symbolic route pattern for analytics.
 *
 * Uses `matchRoutes` purely to find which pattern matches — the bound param
 * values (org/prog ids, case ids, etc.) are discarded; only `route.path`
 * (the pattern string itself) is ever returned. This guarantees no id or
 * PHI-bearing URL segment reaches analytics, even though live URLs carry
 * `org-`/`prog-` prefixed ids in the `:orgId`/`:progId` segments.
 */
export function resolveRoutePattern(pathname: string): string {
  const matches = matchRoutes(ROUTE_OBJECTS, pathname);
  if (matches && matches.length > 0) {
    const matched = matches[matches.length - 1].route.path;
    if (matched) return matched.startsWith('/') ? matched : `/${matched}`;
  }
  return 'unknown';
}
